import React from 'react';
import ReactDOM from 'react-dom';
import { findIndex, isEmpty } from 'lodash';
import log from 'chameleon/packages/blue-book-player/utility/log';
import CrossOutToolbar from 'chameleon/packages/blue-book-player/CrossOut/CrossOutToolbar';
import CrossOutQuestionToggle from 'chameleon/packages/blue-book-player/CrossOut/CrossOutQuestionToggle';
import ColumnExpander from 'chameleon/packages/blue-book-player/InnerContainer/ColumnExpander';
import styles from 'chameleon/packages/blue-book-player/CrossOut/CrossOutToggle.css';
import { MathematicsSubjects } from 'chameleon/packages/blue-book-player/constants/subjects';
import {
  MATHEMATICS_CHARACTER_MAP_ICON,
  MATHEMATICS_CHARACTER_MAPS,
} from 'chameleon/packages/blue-book-player/constants/mathematics';
import { LcConstants } from 'chameleon/packages/blue-book-player/constants/lcConstants';
import findFirstTextNode from 'chameleon/packages/blue-book-player/utility/findFirstTextNode';
import { SELECTORS } from './constants/selectors';
import MobileContentTabs from 'chameleon/packages/blue-book-player/mobile/MobileContentTabs';

export const getItemByRef = (container, reference) =>
  container.querySelector(`.learnosity-item[data-reference="${reference}"]`);

export const renderToolbar = (el, props) =>
  ReactDOM.createPortal(<CrossOutToolbar {...props} />, el);

export const renderCrossOutOption = (el, props) =>
  ReactDOM.createPortal(<CrossOutQuestionToggle {...props} />, el);

export const renderExpander = (el, props) =>
  ReactDOM.createPortal(<ColumnExpander {...props} />, el);

export const renderContentTabs = (el, props) =>
  ReactDOM.createPortal(<MobileContentTabs {...props} />, el);

/*
 * @todo - refactor createCrossoutContainer into createContainer
 * */
export const createCrossoutContainer = (
  reference,
  playerEl,
  contentSelector,
  className,
) => {
  try {
    const aboveQuestionContentEl = getItemByRef(
      playerEl,
      reference,
    )?.querySelector(contentSelector);
    if (aboveQuestionContentEl) {
      const div = document.createElement('div');
      div.classList.add('crossout-container', 'crossout-padding');
      if (className) {
        div.classList.add(className);
      }
      aboveQuestionContentEl?.parentElement?.insertBefore(
        div,
        aboveQuestionContentEl,
      );
      return Promise.resolve(
        aboveQuestionContentEl?.parentElement?.querySelector(
          SELECTORS.CROSSOUT_CONTAINER,
        ),
      );
    }
  } catch (e) {
    log.error(e);
  }

  return Promise.reject();
};

export const createContainer = (
  locationSelector,
  reference,
  playerEl,
  className,
  injectMethod = 'appendChild',
  containerEl = 'div',
) => {
  try {
    const locationElements = getItemByRef(playerEl, reference).querySelectorAll(
      locationSelector,
    );
    if (locationElements?.length) {
      const containers = [];
      locationElements.forEach((locationElement) => {
        const container = document.createElement(containerEl);
        if (className) {
          container.classList.add(className);
        }
        locationElement[injectMethod](container);
        containers.push({
          triggerContainer: container,
          optionContainer: locationElement,
        });
        return false;
      });
      return Promise.resolve(containers);
    }
  } catch (e) {
    log.error(e);
  }
  return Promise.reject();
};

export const createContainerInElement = (
  locationElement,
  classNames = [],
  injectMethod = 'prepend',
  containerEl = 'div',
) => {
  if (!locationElement) {
    return;
  }
  const container = document.createElement(containerEl);
  if (classNames?.length) {
    classNames.forEach((className) => {
      container.classList.add(className);
    });
  }
  if (locationElement[injectMethod]) {
    locationElement[injectMethod](container);
  }

  return Promise.resolve(container);
};

export const getQuestionByReference = (reference, questions) =>
  questions.find((question) => question.learnosityItemReference === reference);

export const addPropsToNodes = (nodes, props) =>
  nodes.map((node) => ({
    ...node,
    children: React.Children.toArray(node.children).map((child) =>
      React.cloneElement(child, props),
    ),
  }));

export const doSetHiddenAnswer = (element) => {
  const OVERRIDE_SELECTION_CLASS = 'override-selection';
  const optionAnswerEl = element.closest(SELECTORS.OPTION_ANSWER_LIST);
  optionAnswerEl.classList.add(OVERRIDE_SELECTION_CLASS);
  element?.click();

  return () => {
    optionAnswerEl.classList.remove(OVERRIDE_SELECTION_CLASS);
  };
};

export const toggleAnswerOptionsCrossOut = (playerEl, enable) => {
  if (!playerEl) {
    return;
  }
  if (enable) {
    playerEl.classList.add(styles['with-crossout']);
    playerEl.classList.remove(styles['without-crossout']);
  } else {
    playerEl.classList.remove(styles['with-crossout']);
    playerEl.classList.add(styles['without-crossout']);
  }
};

export const setCrossedOutA11y = (inputEl, labelEl) => {
  const forAttr = labelEl.getAttribute('for');
  const labelId = `${forAttr}_label`;
  labelEl.setAttribute('id', labelId);

  const attr = 'aria-labelledby';
  const ariaLabeledBy = inputEl.getAttribute(attr);
  inputEl.setAttribute(attr, `${labelId} ${ariaLabeledBy}`);
};
export const clearCrossedOutA11y = (inputEl, labelEl) => {
  if (!labelEl || !inputEl) return;

  const labelId = labelEl.getAttribute('id');
  labelEl.removeAttribute('id');

  const attr = 'aria-labelledby';
  const ariaLabeledBy = inputEl.getAttribute(attr);
  inputEl.setAttribute(attr, ariaLabeledBy?.replace(labelId, ''));
};

export const setMcqRegionA11y = (playerEl) => {
  const answersGroupEl = playerEl?.querySelector(
    `${SELECTORS.QUESTION_RESPONSES} div[role="group"]`,
  );
  if (answersGroupEl) {
    const labeledByAttr = 'aria-labelledby';
    const labeledById = answersGroupEl.getAttribute(labeledByAttr);
    const srOnlyEl = playerEl.querySelector(`#${labeledById}`);
    if (srOnlyEl) {
      srOnlyEl.parentNode.removeChild(srOnlyEl);
    }
    answersGroupEl.removeAttribute(labeledByAttr);
    answersGroupEl.setAttribute('role', 'region');
    answersGroupEl.setAttribute('aria-label', 'Answer Options');
  }

  return answersGroupEl;
};

export const setMcqRegionAriaLabel = (answersGroupEl, crossedOutCount) => {
  const ariaLabel =
    crossedOutCount === 1
      ? `${crossedOutCount} answer choice is crossed out`
      : crossedOutCount > 0
        ? `${crossedOutCount} answer choices are crossed out`
        : 'no answer choice is crossed out';

  answersGroupEl.setAttribute('aria-label', `Answer Options: ${ariaLabel}`);
};

export const clearMcqRegionAriaLabel = (answersGroupEl) => {
  answersGroupEl.setAttribute('aria-label', `Answer Options`);
};

/**
 * Gets char
 * @param {string} letter
 * @return @next() Returns next alphabetic character
 * @return @prev() Returns previous alphabetic character
 * */
export const alphabeticChar = (letter) => {
  const getChar = (index) => String.fromCharCode(letter.charCodeAt(0) + index);

  return {
    next() {
      return getChar(+1);
    },
    prev() {
      return getChar(-1);
    },
  };
};

/**
 * Format a given time in seconds to MM:SS
 * @param {time: Number}
 **/
export const formatTime = (time, a11y = false) => {
  const timeInt = parseInt(time, 10);
  if (Number.isNaN(timeInt)) {
    return `00:00`;
  }
  // because the time can overflow because extend time and milliseconds, etc.
  // a fair bit of logic is necessary to format this string in a way that doesn't
  // inject NaN values.
  let minutes = Math.floor(timeInt / 60);
  if (Number.isNaN(minutes) || minutes < 0) {
    minutes = 0;
  }
  let seconds = timeInt - minutes * 60;
  if (Number.isNaN(seconds) || seconds < 0) {
    seconds = 0;
  }

  let minutesString = `0${minutes}`;
  const secondsString = `0${seconds}`;
  const minutesLength = minutesString.length;
  minutesString =
    minutesLength >= 4
      ? minutesString.substr(1 - minutesLength)
      : minutesString.substr(-2);
  return a11y
    ? `${minutesString} minutes ${secondsString.substr(-2)} seconds`
    : `${minutesString}:${secondsString.substr(-2)}`;
};

export const getMathematicsSubjectsConfig = (subjectId) =>
  MathematicsSubjects.includes(subjectId)
    ? {
        questions_api_init_options: {
          labelBundle: {
            characterMathMapTitle: '',
            characterMapMathIcon: MATHEMATICS_CHARACTER_MAP_ICON,
            characterMapMathCharacters: MATHEMATICS_CHARACTER_MAPS,
          },
        },
      }
    : null;

export const isImpersonating = () =>
  window.localStorage.getItem(LcConstants.RO) === 'true';

export const createColumnsGutter = (columnEl) => {
  const gutter = document.createElement('div');
  gutter.classList.add('w-[4px]');
  gutter.classList.add('bg-bluebook-gray3');
  gutter.classList.add('float-left');
  gutter.classList.add('columns-gutter');
  columnEl?.after?.(gutter);
};

export const isAnsweredQuestion = (answerData) => {
  const { type, value, wordCount, length } = answerData;

  if (type === 'string') {
    return wordCount > 0 || /<img([\w\W]+?)\/?>/.test(value?.trim());
  }

  if (type === 'array') {
    return value?.length > 0;
  }

  if (type === 'audio+png') {
    return length > 0;
  }

  return true;
};

export const getQuestionsAnswerState = (
  learnosityItems,
  learnosityItemsArray,
  responses,
) => {
  const questionDetails = learnosityItemsArray.map((itemReference) => {
    const item = learnosityItems[itemReference];

    if (item.status === 'not_attempted') {
      return {
        id: itemReference,
        isAnswered: false,
      };
    }

    const itemResponses = item.response_ids;
    const isAnswered = itemResponses.some((responseId) => {
      const response = responses?.[responseId];
      if (isEmpty(response)) {
        return false;
      }
      return isAnsweredQuestion(response);
    });

    return {
      id: itemReference,
      isAnswered,
    };
  });
  return questionDetails;
};

/**
 * Given a list of allwed Calculator Items check
 * if an itemId is allowed calculator to be use
 * @param {allowedItems} calculatorAllowedItems
 * @params {itemId} itemId
 */
export const isCalculatorAllowed = (allowedItems, itemId) => {
  if (!allowedItems) {
    return false;
  }
  const predicate = (element) => element.learnosityItemReference === itemId;
  return allowedItems.some(predicate);
};

export const getFirstNodeTextPos = (element, blackListedClasses = []) => {
  if (!element) return;

  const textNode = findFirstTextNode(element, blackListedClasses);
  let top;
  let left;
  if (textNode) {
    const range = document.createRange();
    range.selectNodeContents(textNode);
    const rects = range.getClientRects();
    ({ left, top } = rects?.[0] || {});
  }

  if (textNode && top !== undefined && left !== undefined) {
    return {
      x: left,
      y: top,
    };
  }

  return null;
};

export const areLearnosityNativeColumns = (firstColumnEl, secondColumnEl) => {
  const TWO_COL_CLASS = 'col-xs-6';
  return (
    firstColumnEl.classList.contains(TWO_COL_CLASS) &&
    secondColumnEl.classList.contains(TWO_COL_CLASS)
  );
};

const getInitialItemRefFromRoute = (itemId, items) => {
  const index = parseInt(itemId, 10);
  const item = items.find((item) => parseInt(item.item_id, 10) === index);
  return item?.learnosity_item_reference;
};

const getIndexByReference = (reference, items) => {
  return findIndex(items, (ref) => ref === reference);
};

const getCurrentIndexByPage = (currentIndex, currentPage, itemsPerPage) => {
  return currentPage * itemsPerPage + currentIndex;
};

export const getIndexOfSingleQuestionPreview = (
  previewData,
  currentItemId = undefined,
  reference = undefined,
) => {
  const { items, itemsPerPage, currentPage } = previewData;
  const itemId = currentItemId || previewData.itemId;
  const cleanItems = items.map((obj) => obj.learnosity_item_reference);
  const itemRef = reference || getInitialItemRefFromRoute(itemId, items);
  const indexByReference = getIndexByReference(itemRef, cleanItems) + 1;
  return getCurrentIndexByPage(indexByReference, currentPage, itemsPerPage);
};

export const isInputElement = (element) => element instanceof HTMLInputElement;

export const checkIfElementsContainEachOther = (elementOne, elementTwo) => {
  return (
    elementOne &&
    elementTwo &&
    (elementOne.contains(elementTwo) || elementTwo.contains(elementOne))
  );
};

export const getValidContentContainers = (questionEl, selectors) => {
  const [passageSelector, questionSelector] = selectors;

  const passageContainerElements = questionEl.querySelectorAll(passageSelector);
  const questionContainerElements =
    questionEl.querySelectorAll(questionSelector);

  const [passageContainerElement] = passageContainerElements || [];
  const [questionContainerElement] = questionContainerElements || [];

  const oneColumnIncludesTheOther = checkIfElementsContainEachOther(
    passageContainerElement,
    questionContainerElement,
  );

  return {
    passageContainerElement,
    questionContainerElement,
    isContainerValid:
      passageContainerElements.length === 1 &&
      questionContainerElements.length === 1 &&
      !oneColumnIncludesTheOther,
  };
};

export const isQuestionContentNotSupported = (questionEl) => {
  const featureWidget = questionEl.querySelector(
    SELECTORS.OPTION_ANSWER_LABEL.FEATURE_WIDGET,
  );
  const multipleAnswerWidgets = questionEl.querySelectorAll(
    SELECTORS.QUESTION_WIDGET,
  );

  return !featureWidget && multipleAnswerWidgets.length > 1;
};
